/**
 * holy-waitlist-intercept.js — 2026-06-06
 * Site-wide pre-launch posture: intercept Wonder's PDP Add-to-Cart + Cart Checkout
 * buttons to a Klaviyo waitlist sign-up instead of completing a sale.
 * Bookings (Ritual Bar) flow is NOT intercepted — only DTC product paths.
 *
 * Selectors (Wonder):
 *   PDP add-to-cart: .js-add-to-cart, [name="add"], .pdp-cta, [data-pdp-cta]
 *   Cart checkout:   [name="checkout"], .cart-foot__cta, [data-checkout-cta], button[name="checkout"], .cart-foot__cta, [data-checkout-cta]
 *
 * Behaviour:
 *   1. Relabel buttons to "Join the waitlist" on page load (preserving Wonder classes).
 *   2. Intercept click → open modal → submit email → POST /apps/booking/waitlist with source.
 *   3. Ritual Bar booking flow stays native (form posts to /apps/booking/draft-order).
 */
(() => {
  'use strict';

  const RELABEL = 'Join the waitlist';
  const ENDPOINT = '/apps/booking/waitlist';

  // ── Force-enable Wonder buy/checkout buttons that ship disabled on sold-out
  //    or unselected-variant — our intercept doesn't care, we're not adding to cart,
  //    and the browser silently swallows click events on disabled <button>, so the modal
  //    never opens. Relabel removed 2026-06-14 per Preston: keep the source label ("Add
  //    to Ritual") visible; click still routes to the waitlist modal below.
  function relabelButtons() {
    document.querySelectorAll('.js-add-to-cart, [name="add"], .pdp-cta, [data-pdp-cta]').forEach(btn => {
      if (btn.disabled) btn.disabled = false;
      if (btn.getAttribute('aria-disabled')) btn.removeAttribute('aria-disabled');
    });
    document.querySelectorAll('[name="checkout"], .cart-foot__cta, [data-checkout-cta]').forEach(btn => {
      if (btn.disabled) btn.disabled = false;
      if (btn.getAttribute('aria-disabled')) btn.removeAttribute('aria-disabled');
    });
  }

  function isPdpButton(el) {
    return !!el.closest('.js-add-to-cart, [name="add"], .pdp-cta, [data-pdp-cta]');
  }
  function isCheckoutButton(el) {
    return !!el.closest('[name="checkout"], .cart-foot__cta, [data-checkout-cta]');
  }

  // ── Modal element + state
  const modal = document.querySelector('[data-holy-waitlist-modal]');
  if (!modal) return; // Snippet not rendered — bail silently.
  const form = modal.querySelector('[data-holy-waitlist-form]');
  const emailInput = modal.querySelector('[data-holy-waitlist-email]');
  const submitBtn = modal.querySelector('[data-holy-waitlist-submit]');
  const savedEl = modal.querySelector('[data-holy-waitlist-saved]');
  const errorEl = modal.querySelector('[data-holy-waitlist-error]');
  const errorMsgEl = modal.querySelector('[data-holy-waitlist-error-msg]');
  let currentSource = 'pdp';

  function openModal(source) {
    currentSource = source;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    form.hidden = false;
    savedEl.hidden = true;
    errorEl.hidden = true;
    submitBtn.disabled = false;
    submitBtn.textContent = RELABEL;
    document.body.style.overflow = 'hidden';
    setTimeout(() => { emailInput?.focus(); }, 60);
  }

  function closeModal() {
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (emailInput) emailInput.value = '';
  }

  // ── Click delegation: catch buy/checkout clicks anywhere in the document
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    // Generic data-attribute trigger (transactions-off mode 2026-06-13):
    // any element with data-holy-waitlist-trigger="{source}" opens the modal.
    // Used by the Booking page "Be first to book" CTA — Phase C booking JS
    // doesn't expose a checkout button selector we can match by class.
    const customTrigger = target.closest('[data-holy-waitlist-trigger]');
    if (customTrigger) {
      e.preventDefault();
      e.stopPropagation();
      openModal(customTrigger.dataset.holyWaitlistTrigger || 'general');
      return;
    }
    if (isPdpButton(target)) {
      e.preventDefault();
      e.stopPropagation();
      openModal('pdp');
    } else if (isCheckoutButton(target)) {
      e.preventDefault();
      e.stopPropagation();
      openModal('cart');
    }
  }, true);

  // ── Close on scrim + close button + Escape
  modal.querySelectorAll('[data-holy-waitlist-close]').forEach(el => {
    el.addEventListener('click', closeModal);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) closeModal();
  });

  // ── Submit handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = (emailInput.value || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      emailInput.focus();
      return;
    }
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving…';
    errorEl.hidden = true;

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          email,
          source: currentSource,
          consent_marketing: true,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[holy-waitlist] failed', res.status, text);
        errorMsgEl.textContent = 'We hit a snag. Try again in a moment.';
        errorEl.hidden = false;
        submitBtn.disabled = false;
        submitBtn.textContent = RELABEL;
        return;
      }
      // Success — swap to saved state
      form.hidden = true;
      errorEl.hidden = true;
      savedEl.hidden = false;
    } catch (err) {
      console.error('[holy-waitlist] error', err);
      errorMsgEl.textContent = "We couldn't reach the waitlist. Check your connection and try again.";
      errorEl.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = RELABEL;
    }
  });

  // ── Initial relabel only. Click delegation (above) catches the click regardless of
  //    label — so any Wonder re-render that drops back to "Add to cart" still intercepts
  //    correctly on click. A throttled re-relabel runs on a few high-signal moments
  //    (variant pick, cart drawer open) instead of a global MutationObserver, which on
  //    Wonder's mutation-heavy DOM (galleries, sticky buy, sliders) was thrashing CPU.
  let relabelTimer = null;
  function scheduleRelabel() {
    if (relabelTimer) return;
    relabelTimer = setTimeout(() => { relabelTimer = null; relabelButtons(); }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relabelButtons);
  } else {
    relabelButtons();
  }

  // Re-relabel on Wonder's own product/cart re-renders. Custom events Wonder fires:
  //   - 'cart:change' / 'cart:update' (cart drawer + page)
  //   - 'product:change' (variant pick)
  // Even if Wonder uses slightly different names, hooking onChange-style events keeps
  // the cost bounded.
  ['cart:change', 'cart:update', 'cart:refresh', 'product:change', 'variant:change']
    .forEach(name => document.addEventListener(name, scheduleRelabel));
})();
